import { onMounted, onUnmounted, reactive } from 'vue'
import { useRouter } from 'vue-router'

/**
 * S6 大屏墙（轮播）状态。
 *
 * 轮播由三层配合完成：
 *  - `/wall` 路由页：开启轮播模式，持有 20s 定时器把路由推到 S2，离开时清理；
 *  - 每个屏页：挂载时若轮播模式仍开启，同样 20s 推到下一屏（卸载即清理定时器）。
 * 这样从任意一屏进入轮播都能持续循环：S1 → S2 → S3 → S4 → S5 → S1 …
 * 轮播开关放 sessionStorage：同一标签页刷新后仍在轮播，新开标签页默认关闭。
 */
export interface WallScreen {
  path: string
  name: string
  label: string
}

export const WALL_SCREENS: WallScreen[] = [
  { path: '/', name: 'S1', label: '经营总览' },
  { path: '/s2', name: 'S2', label: '佣金与对账' },
  { path: '/s3', name: 'S3', label: '团队与晋升' },
  { path: '/s4', name: 'S4', label: '数据接入健康' },
  { path: '/s5', name: 'S5', label: 'AI 使用' },
]

/** 每屏停留时长（ms）。 */
export const WALL_INTERVAL_MS = 20 * 1000

const WALL_STORAGE_KEY = 'datax_wall'

function readWallMode(): boolean {
  try {
    return window.sessionStorage.getItem(WALL_STORAGE_KEY) === '1'
  } catch (err) {
    return false
  }
}

export const wallState = reactive({ enabled: readWallMode() })

export function enableWall(): void {
  wallState.enabled = true
  try {
    window.sessionStorage.setItem(WALL_STORAGE_KEY, '1')
  } catch (err) {
    // 隐私模式下退化为本次会话内存状态
  }
}

export function disableWall(): void {
  wallState.enabled = false
  try {
    window.sessionStorage.removeItem(WALL_STORAGE_KEY)
  } catch (err) {
    // ignore
  }
}

/** 当前路径在轮播序列中的序号（未知路径按第一屏算）。 */
export function wallScreenIndex(path: string): number {
  const index = WALL_SCREENS.findIndex((item) => item.path === path)
  return index < 0 ? 0 : index
}

/** 下一屏路径。 */
export function nextWallPath(path: string): string {
  return WALL_SCREENS[(wallScreenIndex(path) + 1) % WALL_SCREENS.length].path
}

/**
 * 屏页接入轮播：轮播模式下 20s 后 `router.push` 下一屏。
 * 定时器随组件卸载清理，避免路由切换后旧屏继续抢跳。
 */
export function useWallCarousel(currentPath: string): void {
  const router = useRouter()
  let timer: number | undefined

  onMounted(() => {
    if (!wallState.enabled) return
    timer = window.setInterval(() => {
      router.push(nextWallPath(currentPath))
    }, WALL_INTERVAL_MS)
  })

  onUnmounted(() => {
    if (timer !== undefined) window.clearInterval(timer)
    timer = undefined
  })
}
